import moment from 'moment';

let regexpSpecialChars = /([\[\]\^\$\|\(\)\\\+\*\?\{\}\=\!])/gi;

export enum RoundMethod
{
	up,
	down
}

export interface MomentOptions
{
	roundMethod?: RoundMethod;
	utc?: boolean;
}

String.prototype.literal = function (): string
{
	return this.replace(regexpSpecialChars, '\\$1');
};

if (!String.prototype.startsWith)
{
	String.prototype.startsWith = function (searchString: string, position?: number): boolean
	{
		let pos = position !== undefined && position > 0 ? position | 0 : 0;
		return this.substring(pos, pos + searchString.length) === searchString;
	};
}

if (!String.prototype.endsWith)
{
	String.prototype.endsWith = function (searchString: string, position?: number): boolean
	{
		if (position === undefined || position > this.length)
		{
			position = this.length;
		}
		return this.substring(position - searchString.length, position) === searchString;
	};
}

if (!String.prototype.padStart)
{
	Object.defineProperty(String.prototype, 'padStart', {
		configurable: true,
		writable: true,
		value: function (targetLength: number, padString: string)
		{
			targetLength = targetLength >> 0; //floor if number or convert non-number to 0;
			padString = String(typeof padString !== 'undefined' ? padString : ' ');
			if (this.length > targetLength)
			{
				return String(this);
			}
			else
			{
				targetLength = targetLength - this.length;
				if (targetLength > padString.length)
				{
					padString += padString.repeat(targetLength / padString.length); //append to original to ensure we are longer than needed
				}
				return padString.slice(0, targetLength) + String(this);
			}
		}
	});
}

if (!String.prototype.padEnd)
{
	Object.defineProperty(String.prototype, 'padEnd', {
		configurable: true,
		writable: true,
		value: function (targetLength: number, padString: string)
		{
			targetLength = targetLength >> 0; //floor if number or convert non-number to 0;
			padString = String(typeof padString !== 'undefined' ? padString : ' ');
			if (this.length > targetLength)
			{
				return String(this);
			}
			else
			{
				targetLength = targetLength - this.length;
				if (targetLength > padString.length)
				{
					padString += padString.repeat(targetLength / padString.length); //append to original to ensure we are longer than needed
				}
				return String(this) + padString.slice(0, targetLength);
			}
		}
	});
}

String.prototype.left = function (str: string): string
{
	let i = this.indexOf(str);
	if (i > -1)
	{
		return this.substring(0, i);
	}
	else
	{
		return this.toString();
	}
};

String.prototype.right = function (str: string): string
{
	let i = this.indexOf(str);
	if (i > -1)
	{
		return this.substring(i + str.length);
	}
	else
	{
		return this.toString();
	}
};

String.prototype.backwardsLeft = function (str: string): string
{
	let i = this.lastIndexOf(str);
	if (i > -1)
	{
		return this.substring(0, i);
	}
	else
	{
		return this.toString();
	}
};

String.prototype.backwardsRight = function (str: string): string
{
	let i = this.lastIndexOf(str);
	if (i > -1)
	{
		return this.substring(i + str.length);
	}
	else
	{
		return this.toString();
	}
};

String.prototype.matches = function (m: string | RegExp): boolean
{
	let res = this.match(m);

	return res !== null && res.length > 0;
};

// @ts-ignore
String.prototype.toMoment = function (options: MomentOptions): _moment.Moment
{
	if (moment)
	{
		if (!options)
		{
			options = {};
		}

		let format, precision;

		if (this.matches(/\d{2}-\d{2}-\d{4}T\d{2}:\d{2}:\d{2}(Z|[\+\-]\d{2}:\d{2})/))
		{
			format = 'DD-MM-YYYY\'T\'HH:mm:ssZ';
			precision = 3;
		} // ISO_OFFSET_DATE_TIME, dutch format
		else if (this.matches(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(Z|[\+\-]\d{2}:\d{2})/))
		{
			format = 'YYYY-MM-DD\'T\'HH:mm:ssZ';
			precision = 3;
		} // ISO_OFFSET_DATE_TIME
		else if (this.matches(/\d{2}-\d{2}-\d{4}T(Z|\+\d{2}:\d{2})/))
		{
			format = 'DD-MM-YYYYZ';
			precision = 0;
		} // ISO_OFFSET_DATE, dutch format
		else if (this.matches(/\d{4}-\d{2}-\d{2}T(Z|\+\d{2}:\d{2})/))
		{
			format = 'YYYY-MM-DDZ';
			precision = 0;
		} // ISO_OFFSET_DATE
		else if (this.matches(/\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}/))
		{
			format = 'DD-MM-YYYY HH:mm:ss';
			precision = 2;
		} // dateTimeFormat
		else if (this.matches(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/))
		{
			format = 'YYYY-MM-DD HH:mm:ss';
			precision = 2;
		} // dbDateTimeFormat
		else if (this.matches(/\d{2}-\d{2}-\d{4} \d{2}:\d{2}/))
		{
			format = 'DD-MM-YYYY HH:mm';
			precision = 1;
		} // dateTimeFormatWithoutSeconds
		else if (this.matches(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}/))
		{
			format = 'YYYY-MM-DD HH:mm';
			precision = 1;
		} // dbDateTimeFormatWithoutSeconds
		else if (this.matches(/\d{2}-\d{2}-\d{4}/))
		{
			format = 'DD-MM-YYYY';
			precision = 0;
		} // dateFormat
		else if (this.matches(/\d{4}-\d{2}-\d{2}/))
		{
			format = 'YYYY-MM-DD';
			precision = 0;
		} // dbDateFormat
		else if (this.matches(/\d{2}:\d{2}:\d{2}/))
		{
			format = 'HH:mm:ss';
			precision = -1;
		} // timeFormat
		else if (this.matches(/\d{2}:\d{2}/))
		{
			format = 'HH:mm';
			precision = -1;
		} // timeFormatWithoutSeconds
		else
		{
			return moment.invalid();
		}

		// @ts-ignore
		let ret = options.utc && precision != 0 && precision != 3 ? moment.utc(this.toString(), format) : moment(this.toString(), format);

		if (precision == 3 && options.utc)
		{
			ret = ret.utc();
		}

		if (options.roundMethod == RoundMethod.up)
		{
			if (precision == 1)
			{
				ret = ret.endOf('second');
			}
			else if (precision == 0)
			{
				ret = ret.endOf('day');
			}
		}
		else if (options.roundMethod == RoundMethod.down)
		{
			if (precision == 1)
			{
				ret = ret.startOf('second');
			}
			else if (precision == 0)
			{
				ret = ret.startOf('day');
			}
		}

		return ret;
	}
	else
	{
		throw new Error('MomentJS library not found!');
	}
};